import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { nameGlobMatches, useDatasetNames } from '@/features/catalog-browser';
import { useImpact, useNodeByDataset } from '@/features/lineage';
import { severityTone, useBindings, useRules } from '@/features/rules';
import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { TextField } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import {
  useClearExpected,
  useEvaluateDataset,
  useFinding,
  useMarkExpected,
  useMuteSeries,
  useRaiseIncident,
  useSetThreshold,
  useUnmuteSeries,
} from '../api/anomaly';
import type { FindingDetail, SeriesRef } from '../api/anomaly';
import {
  bannerTone,
  baselineFooter,
  primaryAction,
  sensitivityLabel,
  signalKey,
  verdictKey,
  verdictTone,
  windowLabel,
} from '../model/presentation';
import { SeriesChart } from './SeriesChart';

/* One anomaly finding: what the statistics decided, the series behind it, the detector, and what a
 * reader can do about it.
 *
 * The verdict drives the page. An `anomalous` verdict gets a danger-toned banner and a "Raise an
 * incident" primary action; every other verdict is silence, so the primary becomes "View
 * executions" — the useful next thing is to look at the rules that run on this table. Mute /
 * mark-expected / edit-detector are standing decisions about the *series*, not this one finding, and
 * say so.
 */

function DefRow({ term, children }: { term: string; children: ReactNode }): ReactElement {
  return (
    <div className="lq-defrow">
      <span className="lq-defrow__term">{term}</span>
      <span>{children}</span>
    </div>
  );
}

function DetectorCard({ detail }: { detail: FindingDetail }): ReactElement {
  const { t } = useTranslation();
  const sensitivity = sensitivityLabel(detail.detector);
  return (
    <Card title={t('anomaly.detail.detector.title')}>
      <DefRow term={t('anomaly.detail.detector.method')}>{detail.detector.method}</DefRow>
      <DefRow term={t('anomaly.detail.detector.window')}>
        {windowLabel(detail.detector)}
      </DefRow>
      <DefRow term={t('anomaly.detail.detector.sensitivity')}>
        <span className="lq-mono">{sensitivity.value}</span>
        {sensitivity.overridden && (
          <span className="lq-dim"> {t('anomaly.detail.detector.overridden')}</span>
        )}
      </DefRow>
    </Card>
  );
}

function ChartCard({ detail }: { detail: FindingDetail }): ReactElement {
  const { t } = useTranslation();
  return (
    <Card title={t('anomaly.detail.chart.title')}>
      {detail.series.length < 2 ? (
        <p className="lq-muted" style={{ margin: 0 }}>
          {t('anomaly.detail.chart.tooFew', { count: detail.series.length })}
        </p>
      ) : (
        <>
          <SeriesChart
            points={detail.series}
            band={detail.baseline_band}
            label={t('anomaly.detail.chart.label')}
          />
          <div className="lq-serieschart__legend">
            <span className="lq-serieschart__key">
              <span className="lq-serieschart__swatch lq-serieschart__swatch--line" />
              {t('anomaly.detail.chart.observed')}
            </span>
            {detail.baseline_band !== null && (
              <span className="lq-serieschart__key">
                <span className="lq-serieschart__swatch lq-serieschart__swatch--band" />
                {t('anomaly.detail.chart.expected')}
              </span>
            )}
          </div>
          <div className="lq-serieschart__foot">
            <span>
              {t('anomaly.detail.chart.periods', { count: detail.series.length })}
            </span>
            <span>{baselineFooter(detail)}</span>
            <span>{t('anomaly.detail.chart.now')}</span>
          </div>
        </>
      )}
    </Card>
  );
}

/* The downstream half of "what this affects". Its own component so `useImpact` only mounts once a
   lineage node has resolved — a query keyed on an empty id would hit `/nodes//impact`. */
function DownstreamList({ nodeId }: { nodeId: string }): ReactElement {
  const { t } = useTranslation();
  const impact = useImpact(nodeId, null);

  if (impact.isPending) return <SkeletonRows rows={2} columns={2} />;

  const noDownstream = (
    <p className="lq-muted" style={{ margin: 0 }}>
      {t('anomaly.detail.affects.noDownstream')}
    </p>
  );
  if (impact.isError) return noDownstream;

  const { affected: downstream, complete } = impact.data;
  if (downstream.length === 0) return noDownstream;

  return (
    <ul className="lq-plainlist">
      {downstream.map((affected) => (
        <li key={affected.node_id}>
          <span className="lq-mono">{affected.display_name}</span>{' '}
          <span className="lq-dim">
            {t(`anomaly.detail.affects.nodeKind.${affected.kind}`)} ·{' '}
            {t('anomaly.detail.affects.hops', { count: affected.depth })}
          </span>
        </li>
      ))}
      {!complete && (
        <li className="lq-dim">{t('anomaly.detail.affects.impactIncomplete')}</li>
      )}
    </ul>
  );
}

function AffectsCard({
  datasetId,
  qualified,
}: {
  datasetId: string;
  qualified: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const bindings = useBindings();
  const rules = useRules('');
  const node = useNodeByDataset(datasetId);

  const rulesById = new Map((rules.data?.items ?? []).map((rule) => [rule.id, rule]));
  const boundRules = (bindings.data?.items ?? []).filter((binding) =>
    binding.selectors.some(
      (selector) =>
        selector.kind === 'name_glob' &&
        qualified !== null &&
        typeof selector.pattern === 'string' &&
        nameGlobMatches(selector.pattern, qualified),
    ),
  );

  return (
    <Card
      title={t('anomaly.detail.affects.title')}
      hint={t('anomaly.detail.affects.hint')}
    >
      <h3 className="lq-subhead">{t('anomaly.detail.affects.rules')}</h3>
      {bindings.isPending ? (
        <SkeletonRows rows={2} columns={2} />
      ) : boundRules.length === 0 ? (
        <p className="lq-muted" style={{ margin: 0 }}>
          {t('anomaly.detail.affects.noRules')}
        </p>
      ) : (
        <DataTable
          caption={t('anomaly.detail.affects.rulesCaption')}
          columns={[
            { key: 'rule', label: t('anomaly.detail.affects.column.rule') },
            { key: 'kind', label: t('anomaly.detail.affects.column.kind') },
            { key: 'severity', label: t('anomaly.detail.affects.column.severity') },
          ]}
        >
          {boundRules.map((binding) => {
            const rule = rulesById.get(binding.rule_id);
            return (
              <tr key={binding.id}>
                <td>
                  {rule === undefined ? (
                    <span className="lq-dim">
                      {t('anomaly.detail.affects.unresolvedRule')}
                    </span>
                  ) : (
                    <Link to={`/rules/${encodeURIComponent(rule.id)}`}>{rule.name}</Link>
                  )}
                </td>
                <td className="lq-muted">
                  {rule === undefined ? '—' : t(`rules.kind.${rule.kind}`)}
                </td>
                <td>
                  <StatusBadge
                    tone={severityTone(binding.severity)}
                    label={t(`rules.severity.${binding.severity}.label`)}
                    title={t(`rules.severity.${binding.severity}.meaning`)}
                  />
                </td>
              </tr>
            );
          })}
        </DataTable>
      )}

      <h3 className="lq-subhead" style={{ marginTop: 'var(--space-5)' }}>
        {t('anomaly.detail.affects.downstream')}
      </h3>
      {node.isPending ? (
        <SkeletonRows rows={2} columns={2} />
      ) : node.isError ? (
        <p className="lq-muted" style={{ margin: 0 }}>
          {t('anomaly.detail.affects.noLineage')}
        </p>
      ) : (
        <DownstreamList nodeId={node.data.id} />
      )}
    </Card>
  );
}

function DetectorModal({
  series: seriesRef,
  currentOverride,
  onClose,
}: {
  series: SeriesRef;
  currentOverride: number | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const setThreshold = useSetThreshold();
  const evaluate = useEvaluateDataset();
  const [value, setValue] = useState(
    currentOverride === null ? '' : String(currentOverride),
  );

  const parsed = value.trim() === '' ? null : Number(value);
  const invalid = parsed !== null && (!Number.isFinite(parsed) || parsed <= 0);

  return (
    <Modal open title={t('anomaly.detail.editDetector.title')} onClose={onClose}>
      <p className="lq-muted">{t('anomaly.detail.editDetector.body')}</p>
      <TextField
        label={t('anomaly.detail.editDetector.field')}
        type="number"
        min={0}
        value={value}
        onChange={setValue}
        hint={t('anomaly.detail.editDetector.fieldHint')}
      />
      {setThreshold.isError && <Alert tone="error">{setThreshold.error.message}</Alert>}
      {evaluate.isError && <Alert tone="error">{evaluate.error.message}</Alert>}
      <div className="lq-form-actions">
        <Button
          tone="primary"
          busy={setThreshold.isPending}
          disabled={invalid}
          onClick={() => {
            setThreshold.mutate(
              { ...seriesRef, override: parsed },
              {
                onSuccess: () => {
                  onClose();
                },
              },
            );
          }}
        >
          {t('anomaly.detail.editDetector.save')}
        </Button>
        <Button
          busy={evaluate.isPending}
          onClick={() => {
            evaluate.mutate(seriesRef.datasetId);
          }}
        >
          {t('anomaly.detail.editDetector.reevaluate')}
        </Button>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
      </div>
      {evaluate.isSuccess && (
        <p className="lq-muted" style={{ marginBottom: 0 }}>
          {t('anomaly.detail.editDetector.reevaluated')}
        </p>
      )}
    </Modal>
  );
}

export function AnomalyDetail(): ReactElement {
  const { t } = useTranslation();
  const { findingId = '' } = useParams<{ findingId: string }>();
  const finding = useFinding(findingId);
  const names = useDatasetNames();
  const raiseIncident = useRaiseIncident();
  const mute = useMuteSeries();
  const unmute = useUnmuteSeries();
  const markExpected = useMarkExpected();
  const clearExpected = useClearExpected();
  const [editingDetector, setEditingDetector] = useState(false);

  if (finding.isPending) {
    return (
      <>
        <p role="status" className="lq-muted">
          {t('anomaly.detail.loading')}
        </p>
        <SkeletonRows rows={4} columns={2} />
      </>
    );
  }

  if (finding.isError) {
    return (
      <>
        <Alert tone="error" title={t('anomaly.detail.unreachable')}>
          {finding.error.message}
        </Alert>
        <p style={{ marginTop: 'var(--space-3)' }}>
          <Link to={SCREEN_PATHS.anomalies}>{t('anomaly.detail.back')}</Link>
        </p>
      </>
    );
  }

  const detail = finding.data;
  const seriesRef: SeriesRef = { datasetId: detail.dataset_id, signal: detail.signal };
  const datasetName = names.data?.byId.get(detail.dataset_id) ?? detail.dataset_id;
  const qualified = names.data?.byId.get(detail.dataset_id) ?? null;
  const state = detail.series_state;
  const action = primaryAction(detail.verdict);
  const incidentId = detail.incident_id ?? raiseIncident.data?.incident_id ?? null;

  return (
    <section aria-labelledby="anomaly-heading">
      <nav aria-label={t('breadcrumbs.label')} className="lq-breadcrumbs">
        <Link to={SCREEN_PATHS.anomalies}>{t('anomaly.title')}</Link>
        <span aria-hidden="true" className="lq-breadcrumbs__sep">
          /
        </span>
        <span aria-current="page">{datasetName}</span>
      </nav>

      <div className="lq-page-header">
        <div>
          <h2 id="anomaly-heading" className="lq-page-header__title lq-mono">
            {datasetName}
          </h2>
          <p className="lq-page-header__lede">
            {[
              t(signalKey(detail.signal)),
              detail.detector.method,
              windowLabel(detail.detector),
            ].join(' · ')}
          </p>
        </div>
        <div className="lq-page-header__actions">
          <StatusBadge
            tone={verdictTone(detail.verdict)}
            label={t(verdictKey(detail.verdict))}
            title={t(`${verdictKey(detail.verdict)}.meaning`)}
          />
        </div>
      </div>

      <Alert tone={bannerTone(detail.verdict)}>{detail.explanation.text}</Alert>

      <div className="lq-toolbar" style={{ marginTop: 'var(--space-4)' }}>
        {action === 'raise-incident' ? (
          incidentId !== null ? (
            <Link className="lq-btn" to={SCREEN_PATHS.incidents}>
              {t('anomaly.detail.incidentOpen')}
            </Link>
          ) : (
            <Button
              tone="primary"
              busy={raiseIncident.isPending}
              onClick={() => {
                raiseIncident.mutate(findingId);
              }}
            >
              {t('anomaly.detail.raiseIncident')}
            </Button>
          )
        ) : (
          <Link className="lq-btn lq-btn--primary" to={SCREEN_PATHS.executions}>
            {t('anomaly.detail.viewExecutions')}
          </Link>
        )}

        <Button
          busy={markExpected.isPending || clearExpected.isPending}
          onClick={() => {
            if (state.expected) clearExpected.mutate(seriesRef);
            else markExpected.mutate(seriesRef);
          }}
        >
          {state.expected
            ? t('anomaly.detail.clearExpected')
            : t('anomaly.detail.markExpected')}
        </Button>

        <Button
          busy={mute.isPending || unmute.isPending}
          onClick={() => {
            if (state.muted) unmute.mutate(seriesRef);
            else mute.mutate(seriesRef);
          }}
        >
          {state.muted ? t('anomaly.detail.unmute') : t('anomaly.detail.mute')}
        </Button>

        <Button
          onClick={() => {
            setEditingDetector(true);
          }}
        >
          {t('anomaly.detail.editDetector.open')}
        </Button>
      </div>

      {(raiseIncident.isError || mute.isError || unmute.isError) && (
        <Alert tone="error">
          {(raiseIncident.error ?? mute.error ?? unmute.error)?.message}
        </Alert>
      )}

      {state.muted && <p className="lq-muted">{t('anomaly.detail.mutedNote')}</p>}
      {state.expected && <p className="lq-muted">{t('anomaly.detail.expectedNote')}</p>}

      <div className="lq-stack" style={{ marginTop: 'var(--space-4)' }}>
        <ChartCard detail={detail} />
        <div className="lq-grid">
          <DetectorCard detail={detail} />
          {detail.verdict === 'insufficient_history' ? (
            <Card title={t('anomaly.detail.affects.title')}>
              <EmptyState
                title={t('anomaly.detail.affects.coldStartTitle')}
                guidance={t('anomaly.detail.affects.coldStartGuidance')}
              />
            </Card>
          ) : (
            <AffectsCard datasetId={detail.dataset_id} qualified={qualified} />
          )}
        </div>
      </div>

      {editingDetector && (
        <DetectorModal
          series={seriesRef}
          currentOverride={state.threshold_override}
          onClose={() => {
            setEditingDetector(false);
          }}
        />
      )}
    </section>
  );
}
